import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { get, isEmpty } from "lodash";
import {
  AutoCompleteField,
  Check,
  CheckGroup,
  FormInput,
  FormTypes
} from "MODULES/shared/components/Forms";
import { Field, Fields, FormSection } from "redux-form";
import {
  COMPANY,
  DROPSHIP,
  AS_REMOVED,
  RESERVE_FOR_SALE_QUANTITY,
  PURCHASE_FOR_STOCK,
  PURCHASE_FOR_REPAIR_ASSIST,
  PURCHASE_FOR_SALE,
  PURCHASE_FOR_REPAIR,
  ALLOCATE_TO_SO,
  PURCHASED_FOR_SOLI_ID
} from "MODULES/PurchaseOrders/constants";
import { Col } from "react-bootstrap";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import { AS_IS } from "MODULES/IncomingQuotes/constants";
import { useSettings } from "APP_ROOT/hooks/useCurrentUser";
import { BUY_AR_FOR_STOCK, RESERVE_FOR_SELLER_ID } from "APP_ROOT/constants";
import api from "APP_ROOT/api";
import {
  EXCHANGE_SRP,
  PART_ID,
  SUGGESTED_RETAIL
} from "MODULES/inventories/constants";
import { getFirstNameLastInitial } from "LIB/formatters/text";
import { shortDateYY } from "LIB/formatters/date";
import { dollars } from "LIB/formatters/currency";
import { CONDITIONS } from "MODULES/SalesOrders/constants";

function PurchaseForCheckboxes({
  asIs,
  purchase_for_stock: { input: stock },
  purchase_for_repair_assist: { input: assist },
  purchase_for_sale: { input: sale },
  purchase_for_repair: { input: repair },
  clearPurchaseFor,
  clearReserveForSaleQty,
  disabled,
  shippingType,
  condition,
  onPurchaseForStockChange,
  onPurchaseForSaleChange,
  onPurchaseForRepair
}) {
  const settings = useSettings();

  return (
    <div className="form-group" style={{ cursor: "pointer" }}>
      <label className="control-label" htmlFor="purchase_for">
        Purchase For <sup className="small text-danger">*</sup>
      </label>
      <div
        id="purchase_for"
        className="btn-group btn-group-justified"
        data-toggle="buttons"
      >
        <Check
          input={stock}
          label="Stock"
          icon="fa-check-circle"
          disabled={
            disabled ||
            [COMPANY, DROPSHIP].includes(shippingType) ||
            (condition === AS_REMOVED &&
              !asIs &&
              !stock.value &&
              !get(settings, BUY_AR_FOR_STOCK))
          }
          onChange={ev => {
            onPurchaseForStockChange(ev);
            clearPurchaseFor(ev, [sale.name, repair.name, assist.name]);
            clearReserveForSaleQty(ev);
          }}
        />
        <Check
          disabled={disabled}
          input={assist}
          label="Repair Assist"
          icon="fa-check-circle"
          onChange={ev => {
            clearPurchaseFor(ev, [sale.name, repair.name, stock.name]);
            clearReserveForSaleQty(ev);
          }}
        />
        <Check
          input={repair}
          disabled={disabled}
          label="Reserve for Repair"
          icon="fa-check-circle"
          onChange={ev => {
            clearPurchaseFor(ev, [stock.name, assist.name]);
            clearReserveForSaleQty(ev);
            onPurchaseForRepair(ev);
          }}
        />
        <Check
          disabled={disabled}
          input={sale}
          label="Reserve for Sale"
          icon="fa-check-circle"
          onChange={ev => {
            clearPurchaseFor(ev, [stock.name, assist.name]);
            clearReserveForSaleQty(!ev);
            onPurchaseForSaleChange(ev);
          }}
        />
      </div>
    </div>
  );
}

PurchaseForCheckboxes.propTypes = {
  asIs: PropTypes.bool,
  clearPurchaseFor: PropTypes.func.isRequired,
  clearReserveForSaleQty: PropTypes.func.isRequired,
  purchase_for_stock: PropTypes.shape({
    input: FormTypes.boolInput.isRequired
  }),
  purchase_for_repair_assist: PropTypes.shape({
    input: FormTypes.boolInput.isRequired
  }),
  purchase_for_sale: PropTypes.shape({ input: FormTypes.boolInput.isRequired }),
  purchase_for_repair: PropTypes.shape({
    input: FormTypes.boolInput.isRequired
  }),
  shippingType: PropTypes.string,
  disabled: PropTypes.bool,
  condition: PropTypes.string,
  onPurchaseForSaleChange: PropTypes.func,
  onPurchaseForStockChange: PropTypes.func
};

PurchaseForCheckboxes.defaultProps = {
  onPurchaseForStockChange: () => null
};

function PlaceholderSalesItems({ lineItems, getField, prefix, setField }) {
  if (!lineItems) return null;
  if (lineItems.length === 0) return null;

  const fieldName = name => [prefix, name].filter(i => !isEmpty(i)).join(".");

  const setValues = lineItem => {
    const mapping = {
      [EXCHANGE_SRP]: lineItem.exchange_fee,
      [SUGGESTED_RETAIL]: lineItem.price_per_unit,
      [RESERVE_FOR_SALE_QUANTITY]: lineItem.quantity === 1 ? 1 : null
    };
    Object.keys(mapping).forEach(f => {
      setField(fieldName(f))(mapping[f]);
    });
  };

  const renderRow = (lineItem, i) => (
    <tr key={i}>
      <td style={{ backgroundColor: "#383838" }}>{i + 1}</td>
      <td className="text-center">
        <input
          type="radio"
          name={PURCHASED_FOR_SOLI_ID}
          className="radio-inline"
          checked={lineItem.id === getField(fieldName(PURCHASED_FOR_SOLI_ID))}
          onChange={() => {
            const field = fieldName(PURCHASED_FOR_SOLI_ID);
            setField(field)(lineItem.id);
            setValues(lineItem);
          }}
        />
      </td>
      <td>{shortDateYY(lineItem.created_at)}</td>
      <td>{lineItem.part.part_number}</td>
      <td>{lineItem.sales_order_code}</td>
      <td>{lineItem.condition && CONDITIONS[lineItem.condition].abbrev}</td>
      <td>
        {lineItem.exchange_sale
          ? dollars(lineItem.exchange_fee)
          : dollars(lineItem.price_per_unit)}
      </td>
      <td>{lineItem.quantity}</td>
      <td>{getFirstNameLastInitial(lineItem.seller.full_name)}</td>
    </tr>
  );

  return (
    <div className="col-md-12 reset-panel-table">
      <table className="table table-striped table-hover table-numbered">
        <thead>
          <tr>
            <th />
            <th />
            <th>Reserved At</th>
            <th>Part #</th>
            <th>SO #</th>
            <th>CD</th>
            <th>Price</th>
            <th>QTY</th>
            <th>Reserved For</th>
          </tr>
        </thead>
        <tbody>{lineItems.map(renderRow)}</tbody>
      </table>
    </div>
  );
}

export function ReserveForSaleFields({
  getFieldValue,
  purchase_for_sale,
  prefix,
  searchBuyers,
  selectedReserveForSeller,
  setFieldValue,
  disabled,
  qtyDisabled
}) {
  if (!purchase_for_sale.input.value) {
    return false;
  }

  const [placeholderSalesItems, setPlaceholderSalesItems] = useState([]);
  const allocateToSo = getFieldValue(
    [prefix, ALLOCATE_TO_SO].filter(f => !isEmpty(f)).join(".")
  );

  const partId = getFieldValue(
    [prefix, PART_ID].filter(f => !isEmpty(f)).join(".")
  );

  useEffect(
    () => {
      if (partId) {
        const params = { placeholder: true, part_id: partId, limit: 100 };
        const currentPlaceHolderId = getFieldValue(
          [prefix, PURCHASED_FOR_SOLI_ID].filter(f => !isEmpty(f)).join(".")
        );

        if (currentPlaceHolderId) {
          params.current_placeholder_id = currentPlaceHolderId;
        }

        api.salesOrders.search(params).then(({ data }) => {
          setPlaceholderSalesItems(data.results);
        });
      } else {
        setPlaceholderSalesItems([]);
      }

      return function cleanup() {};
    },
    [partId]
  );

  const hasPlaceholders = placeholderSalesItems.length > 0;

  return [
    <FormRow>
      <Col md={4}>
        <Field
          name={RESERVE_FOR_SELLER_ID}
          label="Reserve for Salesperson"
          placeholder="Search by name..."
          component={AutoCompleteField}
          search={searchBuyers}
          selectedItem={selectedReserveForSeller}
          formatItem={result => result && result.full_name}
          disabled={disabled}
          required
        />
      </Col>
      <Col md={4}>
        <Field
          name={RESERVE_FOR_SALE_QUANTITY}
          component={FormInput}
          label="Reserve QTY"
          type="number"
          disabled={qtyDisabled}
          required
          inputProps={{
            min: 1
          }}
        />
      </Col>
      {hasPlaceholders && (
        <Col md={3}>
          <CheckGroup title="Allocate to Existing SO">
            <Field
              component={Check}
              name={ALLOCATE_TO_SO}
              label="Allocate to Existing SO"
              disabled={!hasPlaceholders}
              onChange={(_ev, val) => {
                if (!val) {
                  setFieldValue(
                    [prefix, PURCHASED_FOR_SOLI_ID]
                      .filter(f => !isEmpty(f))
                      .join(".")
                  )(null);
                }
              }}
            />
          </CheckGroup>
        </Col>
      )}
    </FormRow>,
    allocateToSo && (
      <FormRow>
        <PlaceholderSalesItems
          prefix={prefix}
          getField={getFieldValue}
          lineItems={placeholderSalesItems}
          setField={setFieldValue}
        />
      </FormRow>
    )
  ];
}

ReserveForSaleFields.propTypes = {
  purchase_for_sale: PropTypes.shape({
    input: FormTypes.boolInput.isRequired
  }),
  disabled: PropTypes.bool,
  qtyDisabled: PropTypes.bool,
  selectedReserveForSeller: PropTypes.shape({}),
  searchBuyers: PropTypes.func.isRequired
};

export default function PurchaseForFields({
  clearPurchaseFor = () => {},
  prefix = "",
  searchBuyers,
  selectedReserveForSeller,
  shippingType,
  disabled,
  getFieldValue = () => {},
  qtyDisabled,
  condition,
  onPurchaseForRepair,
  onPurchaseForStockChange,
  onPurchaseForSaleChange,
  setFieldValue
}) {
  const clearReserveForSaleQty = ev => {
    if (ev) {
      const field = [prefix, RESERVE_FOR_SALE_QUANTITY]
        .filter(f => !isEmpty(f))
        .join(".");
      setFieldValue(field)(0);
    }
  };

  return (
    <FormSection name={prefix}>
      <FormRow>
        <Col md={12}>
          <Fields
            names={[
              PURCHASE_FOR_STOCK,
              PURCHASE_FOR_REPAIR_ASSIST,
              PURCHASE_FOR_SALE,
              PURCHASE_FOR_REPAIR
            ]}
            asIs={getFieldValue(
              [prefix, AS_IS].filter(f => !isEmpty(f)).join(".")
            )}
            clearPurchaseFor={clearPurchaseFor}
            component={PurchaseForCheckboxes}
            shippingType={shippingType}
            disabled={disabled}
            condition={condition}
            onPurchaseForStockChange={onPurchaseForStockChange}
            onPurchaseForSaleChange={onPurchaseForSaleChange}
            onPurchaseForRepair={onPurchaseForRepair}
            required
            clearReserveForSaleQty={clearReserveForSaleQty}
          />
        </Col>
      </FormRow>
      <Fields
        component={ReserveForSaleFields}
        disabled={disabled}
        getFieldValue={getFieldValue}
        setFieldValue={setFieldValue}
        names={[PURCHASE_FOR_SALE]}
        partId={getFieldValue(
          [prefix, PART_ID].filter(f => !isEmpty(f)).join(".")
        )}
        prefix={prefix}
        qtyDisabled={qtyDisabled}
        searchBuyers={searchBuyers}
        selectedReserveForSeller={selectedReserveForSeller}
      />
    </FormSection>
  );
}

PurchaseForFields.propTypes = {
  clearPurchaseFor: PropTypes.func,
  condition: PropTypes.string,
  getFieldValue: PropTypes.func,
  prefix: PropTypes.string,
  shippingType: PropTypes.string,
  disabled: PropTypes.bool,
  qtyDisabled: PropTypes.bool,
  searchBuyers: PropTypes.func.isRequired,
  selectedReserveForSeller: PropTypes.shape({}),
  setFieldValue: PropTypes.func.isRequired,
  onPurchaseForRepair: PropTypes.func,
  onPurchaseForSaleChange: PropTypes.func,
  onPurchaseForStockChange: PropTypes.func
};

PurchaseForCheckboxes.defaultProps = {
  onPurchaseForStockChange: () => null,
  onPurchaseForSaleChange: () => null
};



// WEBPACK FOOTER //
// ./app/modules/PurchaseOrders/shared/PurchaseForFields.jsx